import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { InsStatus } from "@/pages/insurer/InsurerLayout";
import { InsuranceSubmission } from "@/components/shared/InsuranceSubmission";
import { InsuranceQuoteBuilder } from "./InsuranceQuoteBuilder";
import { InsuranceThread } from "@/components/shared/InsuranceThread";
import { INS_STATUSES, harareToday } from "@/lib/insurance";
import { API, api, errMsg, fmtDate, getToken } from "@/lib/api";

const photoUrl = (id) => `${API}/admin/insurance/files/${id}?auth=${getToken() || ""}`;
const MANUAL = INS_STATUSES.filter(([k]) => !["quote_ready", "quote_accepted", "quote_declined"].includes(k));
const box = "rounded-2xl bg-white p-5";

const Controls = ({ r, staff, reload }) => {
  const [sched, setSched] = useState({ date: "", slot: "morning" });
  const patch = async (body) => {
    try { await api.patch(`/admin/insurance/requests/${r.id}`, body); toast.success("Updated"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const schedule = async () => {
    if (!sched.date) return toast.error("Pick a date");
    try { await api.post(`/admin/insurance/requests/${r.id}/schedule`, sched); toast.success("Assessment booked on the calendar"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const convert = async () => {
    try { const res = await api.post(`/admin/insurance/requests/${r.id}/convert-job`); toast.success(`Job card ${res.data.job_reference} created`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const isManual = MANUAL.some(([k]) => k === r.status);
  return (
    <section className={`${box} space-y-4`} data-testid="admin-insurance-controls">
      <label className="block text-sm space-y-1"><span className="text-xs font-semibold uppercase tracking-wider text-[#56585E]">Assigned to</span>
        <select value={r.assigned_to?.id || ""} onChange={(e) => patch({ assigned_to: e.target.value || null })} className="h-10 w-full rounded-md border px-3" data-testid="admin-insurance-assign">
          <option value="">Unassigned</option>{staff.map((s) => <option key={s.id} value={s.id}>{s.name || s.email}</option>)}
        </select></label>
      <label className="block text-sm space-y-1"><span className="text-xs font-semibold uppercase tracking-wider text-[#56585E]">Status</span>
        <select value={isManual ? r.status : ""} onChange={(e) => e.target.value && patch({ status: e.target.value })} className="h-10 w-full rounded-md border px-3" data-testid="admin-insurance-status-select">
          {!isManual && <option value="">{INS_STATUSES.find(([k]) => k === r.status)?.[1]} (set by quote)</option>}
          {MANUAL.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select></label>
      <div className="space-y-2"><p className="text-xs font-semibold uppercase tracking-wider text-[#56585E]">Schedule assessment</p>
        {r.assessment && <p className="text-sm" data-testid="admin-insurance-assessment">Booked {fmtDate(r.assessment.date)} ({r.assessment.slot}) · {r.assessment.booking_reference}</p>}
        <div className="flex gap-2">
          <Input type="date" value={sched.date} min={harareToday()} onChange={(e) => setSched({ ...sched, date: e.target.value })} data-testid="admin-insurance-schedule-date" />
          <select value={sched.slot} onChange={(e) => setSched({ ...sched, slot: e.target.value })} className="h-10 rounded-md border px-2 text-sm" data-testid="admin-insurance-schedule-slot"><option value="morning">Morning</option><option value="afternoon">Afternoon</option></select>
        </div>
        <button onClick={schedule} className="rounded-full bg-[#020165] px-4 py-2 text-sm font-semibold text-white" data-testid="admin-insurance-schedule-button">{r.assessment ? "Reschedule" : "Book assessment"}</button>
      </div>
      {r.status === "quote_accepted" && <button onClick={convert} className="w-full rounded-full bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white" data-testid="admin-insurance-convert-job">Convert to job card</button>}
      {r.job_reference && <p className="text-sm" data-testid="admin-insurance-job-ref">Job card: <Link to="/admin/board" className="font-semibold underline">{r.job_reference}</Link></p>}
    </section>
  );
};

const Notes = ({ r, reload }) => {
  const [body, setBody] = useState("");
  const add = async (e) => {
    e.preventDefault();
    try { await api.post(`/admin/insurance/requests/${r.id}/notes`, { body }); setBody(""); reload(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <section className={box} data-testid="admin-insurance-notes">
      <h2 className="flex items-center gap-2 font-display font-bold"><Lock className="h-4 w-4" />Internal notes</h2>
      <p className="text-xs text-[#56585E] mb-3">Never visible to the insurer.</p>
      <ul className="space-y-2 max-h-60 overflow-y-auto">{(r.internal_notes || []).map((n) => <li key={n.id} className="border-l-2 border-[#F86900] pl-3 text-sm"><p className="whitespace-pre-line">{n.body}</p><p className="text-[11px] text-[#56585E]">{n.by} · {new Date(n.at).toLocaleString("en-ZW")}</p></li>)}</ul>
      <form onSubmit={add} className="mt-3 flex gap-2"><Input required value={body} onChange={(e) => setBody(e.target.value)} placeholder="Add a note" data-testid="admin-insurance-note-input" /><button className="rounded-full bg-[#020165] px-4 text-sm font-semibold text-white" data-testid="admin-insurance-note-submit">Add</button></form>
    </section>
  );
};

const Audit = ({ r }) => (
  <section className={box} data-testid="admin-insurance-audit">
    <h2 className="font-display font-bold mb-3">Audit trail</h2>
    <ul className="space-y-1.5 text-xs max-h-72 overflow-y-auto">
      {[...(r.audit || [])].reverse().map((a) => (
        <li key={a.id} className="flex flex-wrap gap-x-2"><span className="font-semibold text-[#0D141A]">{a.action.replace(/_/g, " ")}{a.status && a.status !== a.action ? ` → ${a.status.replace(/_/g, " ")}` : ""}</span><span className="text-[#56585E]">{a.actor_name} ({a.actor_type}) · {new Date(a.at).toLocaleString("en-ZW")}</span></li>
      ))}
    </ul>
  </section>
);

export default function AdminInsuranceDetail() {
  const { id } = useParams();
  const [r, setR] = useState(null);
  const [staff, setStaff] = useState([]);
  const [ver, setVer] = useState(0);
  const load = useCallback(() => { api.get(`/admin/insurance/requests/${id}`).then((res) => { setR(res.data.request); setVer((v) => v + 1); }); }, [id]);
  useEffect(() => { load(); api.get("/admin/insurance/staff").then((res) => setStaff(res.data.staff)); }, [load]);
  if (!r) return null;
  return (
    <div className="space-y-5" data-testid="admin-insurance-detail">
      <Link to="/admin/insurance" className="inline-flex items-center gap-1 text-sm text-[#56585E] hover:text-[#020165]"><ArrowLeft className="h-4 w-4" />Insurance inbox</Link>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-display text-3xl font-extrabold tracking-tight">{r.claim_number}</h1>
        <InsStatus status={r.status} testId="admin-insurance-status" />
        <span className="text-sm text-[#56585E]">{r.company_name} · {r.reference} · received {fmtDate(r.created_at)}</span>
      </div>
      <div className="grid gap-5 xl:grid-cols-[1fr_340px]">
        <div className="space-y-5 min-w-0">
          <InsuranceQuoteBuilder key={ver} r={r} onSaved={load} />
          <InsuranceThread client={api} basePath={`/admin/insurance/requests/${r.id}`} uploadPath={`/admin/insurance/requests/${r.id}/files`} fileUrl={photoUrl} me="staff" title="Messages with insurer" />
          <InsuranceSubmission r={r} photoUrl={photoUrl} />
        </div>
        <div className="space-y-5"><Controls r={r} staff={staff} reload={load} /><Notes r={r} reload={load} /><Audit r={r} /></div>
      </div>
    </div>
  );
}
